import { useState } from "react";
import { useForm } from "react-hook-form";
import supabase from "../../configs/supabase/client";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FaSpinner } from "react-icons/fa";
import Snackbar from "../../components/Snackbar";
import Footer from "../../components/Footer";
import { useNavigate } from "react-router-dom";

type ForgotPasswordProps = {
  email: string;
};

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [isError, setIsError] = useState(false);

  const navigate = useNavigate();

  const schema = yup.object().shape({
    email: yup
      .string()
      .email("Invalid email address")
      .required("Email is required"),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<ForgotPasswordProps>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: ForgotPasswordProps) => {
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(data.email);
    if (error) {
      console.error("Error verifying email:", error);
      setIsError(true);
      setOpen(true);
    } else {
      setOpen(true);
      setIsError(false);
      reset();
    }
    setLoading(false);
  };

  return (
    <form className="font-[Outfit]" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex justify-center">
        <div className="flex flex-1 max-w-xs mt-8">
          <div className="flex flex-col justify-center w-full mt-14">
            <h1 className="text-2xl text-gray-800 mb-5 font-bold">
              Forgot Password?
            </h1>
            <div className="mt-5 mb-2">
              <label
                className="block text-gray-500 font-normal mb-1"
                htmlFor="email"
              >
                Email
              </label>
              <input
                className={`w-full text-sm bg-gray-50 text-gray-500 px-2 py-3 rounded-md border mb-2 outline-none focus:border-gray-400 ${
                  errors.email ? "border-red-300" : ""
                }`}
                type="text"
                {...register("email")}
                autoComplete="off"
              />
              {errors.email && (
                <p className="text-red-400 text-xs mb-1">
                  {errors.email.message}
                </p>
              )}
            </div>
            <button
              type="submit"
              className={`flex justify-center items-center text-md h-12 w-full mt-3 py-3 bg-dark-bg text-gray-50 rounded-md outline-none ${
                !isValid || loading
                  ? "opacity-70 cursor-auto"
                  : "cursor-pointer"
              }`}
              disabled={!isValid || loading}
            >
              {!loading ? (
                "Verify email"
              ) : (
                <FaSpinner className="animate-spin" />
              )}
            </button>
            <div className="flex justify-center text-gray-700 text-xs text-center mt-2 mb-5">
              Remember Password?
              <div
                onClick={() => navigate("/login")}
                className="ml-1 font-bold text-grey-900 underline underline-offset-4 cursor-pointer"
              >
                Login
              </div>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={open}
        close={() => {
          setOpen(false);
          navigate("/builder");
        }}
        variant={`${isError ? "error" : "success"}`}
        message={`${
          isError ? "Email not found" : "Reset password email has been sent"
        }`}
        timeout={5}
      />
      <Footer />
    </form>
  );
};

export default ForgotPassword;
